/**
 * Surface tool operations slice for the Zustand store.
 *
 * Owns the four Object Mode surface tools (Box, Cylinder, Poly, Inset).
 * Mirrors the slice-by-domain pattern of `storeObjectOps.ts` /
 * `storeCommandOps/`.
 *
 * The four tools share an identical confirm ceremony — validate draft →
 * build SceneObject → commit through `commitSurfaceObjectWithOptionalBoolean`
 * → push undo → disarm. Only the SceneObject construction differs per kind,
 * so the ceremony lives in `runSurfaceConfirm` and per-kind closures live
 * in `SURFACE_TOOL_BUILDERS` (mirrors the `defineGenerator` registry shape
 * elsewhere in the codebase).
 */

import type { BlendModifier } from "../blend-reader/types/BlendModifier.ts";
import { eulerXYZFromBasis } from "../modeling/faceBasis.ts";
import {
  buildSurfaceGeneratorMesh,
  SURFACE_BOX_GENERATOR_ID,
  SURFACE_CYLINDER_GENERATOR_ID,
  SURFACE_INSET_GENERATOR_ID,
  SURFACE_POLY_GENERATOR_ID,
  surfaceBoxParamsFromSpec,
  surfaceCylinderParamsFromSpec,
  surfaceGeneratorRef,
  surfaceInsetOffsetLoop,
  surfaceInsetParamsFromDraft,
  surfacePolyParamsFromSpec,
} from "../modeling/generators/surface.ts";
import { basisUV, cubeTransformFromDraft } from "../modeling/surfaceBoxMath.ts";
import { SURFACE_CONFIRM_EPSILON } from "../modeling/surfaceConstants.ts";
import { surfaceCutStartOffset } from "../modeling/surfaceCutOffset.ts";
import type { SurfaceCutterSpec } from "../modeling/surfaceHollowCutter.ts";
import { polygonSignedArea } from "../modeling/surfacePolyMath.ts";
import {
  linearRampProfileFromEllipseEdge,
  type SurfaceCutterProfile,
  sampleSurfaceRectangleLoop,
  surfaceProfileFromTarget,
} from "../modeling/surfaceProfiles.ts";
import type { SceneObject, ShadingMode } from "../modeling/types.ts";
import {
  createDefaultMaterialSlots,
  createSceneObjectId,
} from "../modeling/types.ts";
import {
  recomputeLoopNormals,
  resolveAutoSmoothAngle,
} from "../wasm/normals.ts";
import { clearLiveEditPreview } from "./liveEditPreview.ts";
import type { SceneObjectMap } from "./sceneObjectMap.ts";
import { buildNextModifierOverrides } from "./storeGeneratorOps.ts";
import type {
  AppState,
  InteractionMode,
  ModifierOverride,
  SurfaceFootprintDraftState,
  SurfaceInsetDraftState,
  SurfacePolyDraftState,
  SurfaceToolKind,
  SurfaceToolState,
} from "./storeTypes.ts";
import { commitSurfaceObjectWithOptionalBoolean } from "./surfaceBooleanCommit.ts";

type Set_ = (partial: Partial<AppState>) => void;
type Get_ = () => AppState;

// ---------------------------------------------------------------------------
// Per-kind SceneObject builders
// ---------------------------------------------------------------------------

/**
 * Common output of every per-kind builder. The ceremony function takes this
 * and runs it through `commitSurfaceObjectWithOptionalBoolean` regardless of
 * which surface tool produced it.
 */
interface BuiltSurfaceObject {
  object: SceneObject;
  hostObjectId: string | null;
  reverse: boolean;
  /** Generator-stack modifiers attached to the new object (e.g. Array). */
  generatorModifiers: readonly BlendModifier[];
}

interface BuilderContext {
  sceneObjects: SceneObjectMap;
  createMaterial: () => string;
}

type SurfaceToolBuilder<K extends SurfaceToolKind> = (
  draft: SurfaceDraftFor<K>,
  ctx: BuilderContext,
) => BuiltSurfaceObject | null;

type SurfaceDraftFor<K extends SurfaceToolKind> = K extends "box" | "cylinder"
  ? SurfaceFootprintDraftState
  : K extends "poly"
    ? SurfacePolyDraftState
    : K extends "inset"
      ? SurfaceInsetDraftState
      : never;

// --- helpers shared between builders ---------------------------------------

function uniqueObjectName(
  sceneObjects: SceneObjectMap,
  prefix: string,
  matchKind: (o: SceneObject) => boolean,
): string {
  const counter = [...sceneObjects.values()].filter(matchKind).length + 1;
  return `${prefix}.${String(counter).padStart(3, "0")}`;
}

function surfaceFootprintProfile(
  kind: "box" | "cylinder",
  target: { kind: "face" } | { kind: "edge"; edgeIndex: number } | undefined,
  width: number,
  depth: number,
): SurfaceCutterProfile {
  if (!target || target.kind === "face") return { kind: "prism" };
  if (kind === "cylinder") {
    return linearRampProfileFromEllipseEdge(
      width / 2,
      depth / 2,
      32,
      target.edgeIndex,
    );
  }
  return surfaceProfileFromTarget(
    sampleSurfaceRectangleLoop(width, depth),
    target,
  );
}

// --- box -------------------------------------------------------------------

function buildSurfaceBoxObject(
  draft: SurfaceFootprintDraftState,
  ctx: BuilderContext,
): BuiltSurfaceObject | null {
  const transform = cubeTransformFromDraft(draft);
  if (
    Math.abs(transform.width) < SURFACE_CONFIRM_EPSILON ||
    Math.abs(transform.depth) < SURFACE_CONFIRM_EPSILON ||
    Math.abs(draft.height) < SURFACE_CONFIRM_EPSILON
  ) {
    return null;
  }

  const reverse = draft.height < 0;
  const cutStartOffset = reverse
    ? surfaceCutStartOffset(Math.abs(draft.height))
    : 0;
  const cutCenterOffset = cutStartOffset / 2;
  const spec: SurfaceCutterSpec = {
    kind: "box",
    width: transform.scale[0],
    depth: transform.scale[1],
    height: transform.scale[2],
    reverse,
    cutStartOffset,
    profile: surfaceFootprintProfile(
      "box",
      draft.profileTarget,
      transform.scale[0],
      transform.scale[1],
    ),
  };
  const params = surfaceBoxParamsFromSpec(spec);
  const mesh = buildSurfaceGeneratorMesh(SURFACE_BOX_GENERATOR_ID, params);
  const shading: ShadingMode = "flat";
  const loopNormals = recomputeLoopNormals(
    mesh.geometry.vertices,
    mesh.geometry.topology,
    resolveAutoSmoothAngle(shading),
  );
  const matId = ctx.createMaterial();
  const object: SceneObject = {
    id: createSceneObjectId(),
    name: uniqueObjectName(
      ctx.sceneObjects,
      "Cube",
      (o) => o.objectKind === "cube",
    ),
    objectKind: "cube",
    position: [
      transform.position[0] + cutCenterOffset * draft.basis.normal[0],
      transform.position[1] + cutCenterOffset * draft.basis.normal[1],
      transform.position[2] + cutCenterOffset * draft.basis.normal[2],
    ],
    rotation: [
      transform.rotation[0],
      transform.rotation[1],
      transform.rotation[2],
    ],
    scale: [1, 1, 1],
    vertices: mesh.geometry.vertices,
    indices: mesh.geometry.indices,
    loopNormals,
    topology: mesh.geometry.topology,
    uvs: mesh.geometry.uvs,
    materialSlots: createDefaultMaterialSlots(matId),
    shading,
    generator: surfaceGeneratorRef(SURFACE_BOX_GENERATOR_ID, params),
  };
  return {
    object,
    hostObjectId: draft.hostObjectId,
    reverse,
    generatorModifiers: mesh.generatorModifiers,
  };
}

// --- cylinder --------------------------------------------------------------

function buildSurfaceCylinderObject(
  draft: SurfaceFootprintDraftState,
  ctx: BuilderContext,
): BuiltSurfaceObject | null {
  const transform = cubeTransformFromDraft(draft);
  if (
    Math.abs(transform.width) < SURFACE_CONFIRM_EPSILON ||
    Math.abs(transform.depth) < SURFACE_CONFIRM_EPSILON ||
    Math.abs(draft.height) < SURFACE_CONFIRM_EPSILON
  ) {
    return null;
  }

  const width = Math.abs(transform.width);
  const depth = Math.abs(transform.depth);
  const height = Math.abs(draft.height);
  const reverse = draft.height < 0;
  const cutStartOffset = reverse ? surfaceCutStartOffset(height) : 0;
  const cutCenterOffset = cutStartOffset / 2;
  const spec: SurfaceCutterSpec = {
    kind: "cylinder",
    radiusX: width / 2,
    radiusY: depth / 2,
    height,
    segments: 32,
    reverse,
    cutStartOffset,
    profile: surfaceFootprintProfile(
      "cylinder",
      draft.profileTarget,
      width,
      depth,
    ),
  };
  const params = surfaceCylinderParamsFromSpec(spec);
  const mesh = buildSurfaceGeneratorMesh(SURFACE_CYLINDER_GENERATOR_ID, params);
  const shading: ShadingMode = "autoSmooth";
  const loopNormals = recomputeLoopNormals(
    mesh.geometry.vertices,
    mesh.geometry.topology,
    resolveAutoSmoothAngle(shading),
  );
  const matId = ctx.createMaterial();
  const object: SceneObject = {
    id: createSceneObjectId(),
    name: uniqueObjectName(
      ctx.sceneObjects,
      "Cylinder",
      (o) => o.objectKind === "cylinder",
    ),
    objectKind: "cylinder",
    position: [
      transform.position[0] + cutCenterOffset * draft.basis.normal[0],
      transform.position[1] + cutCenterOffset * draft.basis.normal[1],
      transform.position[2] + cutCenterOffset * draft.basis.normal[2],
    ],
    rotation: [
      transform.rotation[0],
      transform.rotation[1],
      transform.rotation[2],
    ],
    scale: [1, 1, 1],
    vertices: mesh.geometry.vertices,
    indices: mesh.geometry.indices,
    loopNormals,
    topology: mesh.geometry.topology,
    uvs: mesh.geometry.uvs,
    materialSlots: createDefaultMaterialSlots(matId),
    shading,
    generator: surfaceGeneratorRef(SURFACE_CYLINDER_GENERATOR_ID, params),
  };
  return {
    object,
    hostObjectId: draft.hostObjectId,
    reverse,
    generatorModifiers: mesh.generatorModifiers,
  };
}

// --- poly ------------------------------------------------------------------

function buildSurfacePolyObject(
  draft: SurfacePolyDraftState,
  ctx: BuilderContext,
): BuiltSurfaceObject | null {
  if (
    !draft.closed ||
    draft.vertices.length < 3 ||
    Math.abs(draft.height) < SURFACE_CONFIRM_EPSILON
  ) {
    return null;
  }

  // Project every vertex into basis (u, v); the prism is built in
  // object-local XY using the centroid as origin so the resulting object
  // can be placed via a clean position + rotation pair (no per-vertex
  // world transform baked into the geometry).
  const uvs = draft.vertices.map((v) => basisUV(draft.basis, v));
  const area = polygonSignedArea(uvs);
  if (Math.abs(area) < SURFACE_CONFIRM_EPSILON) {
    // Degenerate polygon (collinear vertices). Cancel without scene change.
    return null;
  }

  // CCW for the cap-normal alignment downstream — `buildExtrudedPolygon`
  // also normalizes winding internally, but we mirror the convention here
  // so the centroid we compute lines up with the geometry's local origin.
  const ordered = area > 0 ? uvs : [...uvs].reverse();
  const cu = ordered.reduce((s, p) => s + p.u, 0) / ordered.length;
  const cv = ordered.reduce((s, p) => s + p.v, 0) / ordered.length;
  const profileXY = ordered.map(
    (p) => [p.u - cu, p.v - cv] as [number, number],
  );

  // The surface generator routes through `buildExtrudedPolygon` for the
  // solid case and adds an inset inner loop (`insetPolygon2D` + earcut hole)
  // for Hollow. The dispatcher owns the local-z layout: forward cuts span
  // [0, |h|+offset] with the cap at +|h|; reverse cuts shift down by |h| so
  // the cut-depth cap ends up at -|h|.
  const thickness = Math.max(SURFACE_CONFIRM_EPSILON, Math.abs(draft.height));
  const reverse = draft.height < 0;
  const cutStartOffset = reverse ? surfaceCutStartOffset(thickness) : 0;
  const spec: SurfaceCutterSpec = {
    kind: "poly",
    outerLoop: profileXY,
    height: thickness,
    reverse,
    cutStartOffset,
    profile: surfaceProfileFromTarget(profileXY, draft.profileTarget),
  };
  const params = surfacePolyParamsFromSpec(spec);
  const mesh = buildSurfaceGeneratorMesh(SURFACE_POLY_GENERATOR_ID, params);

  // Object position: anchor the geometry's local origin (z = 0) at the
  // basis-plane centroid. Positive prisms span local z ∈ [0, |h|];
  // negative prisms span from the requested cut depth to the start-cap
  // offset above the host.
  const center: [number, number, number] = [
    draft.basis.origin[0] +
      cu * draft.basis.tangent[0] +
      cv * draft.basis.bitangent[0],
    draft.basis.origin[1] +
      cu * draft.basis.tangent[1] +
      cv * draft.basis.bitangent[1],
    draft.basis.origin[2] +
      cu * draft.basis.tangent[2] +
      cv * draft.basis.bitangent[2],
  ];

  const rotation = eulerXYZFromBasis(draft.basis);
  const shading: ShadingMode = "flat";
  const loopNormals = recomputeLoopNormals(
    mesh.geometry.vertices,
    mesh.geometry.topology,
    resolveAutoSmoothAngle(shading),
  );
  const matId = ctx.createMaterial();
  const object: SceneObject = {
    id: createSceneObjectId(),
    name: uniqueObjectName(
      ctx.sceneObjects,
      "Poly",
      (o) => o.objectKind === "imported" && o.name.startsWith("Poly."),
    ),
    objectKind: "imported",
    position: center,
    rotation,
    scale: [1, 1, 1],
    vertices: mesh.geometry.vertices,
    indices: mesh.geometry.indices,
    loopNormals,
    topology: mesh.geometry.topology,
    uvs: mesh.geometry.uvs,
    materialSlots: createDefaultMaterialSlots(matId),
    shading,
    generator: surfaceGeneratorRef(SURFACE_POLY_GENERATOR_ID, params),
  };
  return {
    object,
    hostObjectId: draft.hostObjectId,
    reverse,
    generatorModifiers: mesh.generatorModifiers,
  };
}

// --- inset -----------------------------------------------------------------

function buildSurfaceInsetObject(
  draft: SurfaceInsetDraftState,
  ctx: BuilderContext,
): BuiltSurfaceObject | null {
  if (
    draft.boundaryLoop.length < 3 ||
    Math.abs(draft.height) < SURFACE_CONFIRM_EPSILON
  ) {
    // Zero or sub-epsilon height — no scene change. Zero inset is fine
    // (footprint becomes the original face boundary).
    return null;
  }

  const thickness = Math.abs(draft.height);
  const reverse = draft.height < 0;
  const cutStartOffset = reverse ? surfaceCutStartOffset(thickness) : 0;
  // Profile is computed against the OFFSET loop (the actual prism footprint
  // the user sees during the height stage), not the original face boundary —
  // edge targets the user clicks on are edges of the inset preview.
  const profile = surfaceProfileFromTarget(
    surfaceInsetOffsetLoop(draft.boundaryLoop, draft.insetDistance),
    draft.profileTarget,
  );
  const params = surfaceInsetParamsFromDraft({
    boundaryLoop: draft.boundaryLoop,
    insetDistance: draft.insetDistance,
    height: thickness,
    reverse,
    cutStartOffset,
    profile,
  });
  const mesh = buildSurfaceGeneratorMesh(SURFACE_INSET_GENERATOR_ID, params);

  const rotation = eulerXYZFromBasis(draft.basis);
  const shading: ShadingMode = "flat";
  const loopNormals = recomputeLoopNormals(
    mesh.geometry.vertices,
    mesh.geometry.topology,
    resolveAutoSmoothAngle(shading),
  );
  const matId = ctx.createMaterial();
  const object: SceneObject = {
    id: createSceneObjectId(),
    name: uniqueObjectName(
      ctx.sceneObjects,
      "Inset",
      (o) => o.objectKind === "imported" && o.name.startsWith("Inset."),
    ),
    objectKind: "imported",
    position: draft.centroid,
    rotation,
    scale: [1, 1, 1],
    vertices: mesh.geometry.vertices,
    indices: mesh.geometry.indices,
    loopNormals,
    topology: mesh.geometry.topology,
    uvs: mesh.geometry.uvs,
    materialSlots: createDefaultMaterialSlots(matId),
    shading,
    generator: surfaceGeneratorRef(SURFACE_INSET_GENERATOR_ID, params),
  };
  return {
    object,
    hostObjectId: draft.hostObjectId,
    reverse,
    generatorModifiers: mesh.generatorModifiers,
  };
}

// ---------------------------------------------------------------------------
// Registry — flat lookup mirroring `defineGenerator` / `GENERATORS`
// ---------------------------------------------------------------------------

const SURFACE_TOOL_BUILDERS: {
  [K in SurfaceToolKind]: SurfaceToolBuilder<K>;
} = {
  box: buildSurfaceBoxObject,
  cylinder: buildSurfaceCylinderObject,
  poly: buildSurfacePolyObject,
  inset: buildSurfaceInsetObject,
};

// ---------------------------------------------------------------------------
// Ceremony
// ---------------------------------------------------------------------------

/**
 * Apply generator-driven Array / Mirror / etc. modifiers to the new
 * SceneObject's overrides map. Mirrors the `mesh.generatorModifiers` flow
 * from each (now-collapsed) per-kind confirm function.
 */
function applyGeneratorModifiers(
  modifierOverrides: Map<string, ModifierOverride[]>,
  objectId: string,
  modifiers: readonly BlendModifier[],
): Map<string, ModifierOverride[]> {
  return modifiers.length > 0
    ? buildNextModifierOverrides(modifierOverrides, objectId, modifiers)
    : modifierOverrides;
}

/**
 * Common confirm ceremony for all four surface tools.
 *
 *   1. Read draft from `surfaceTool`; bail (and disarm) when absent or
 *      kind-mismatched.
 *   2. Run the per-kind builder. A `null` return means the draft was
 *      sub-epsilon / degenerate — disarm cleanly with no scene change.
 *   3. Run `commitSurfaceObjectWithOptionalBoolean` (forward extrusions
 *      add a visible cutter; reverse extrusions hide the cutter and
 *      append a Boolean Difference modifier to the host).
 *   4. Push undo, apply commit result, disarm `surfaceTool`, and clear
 *      `lastOperation` so the redo panel does not appear after a surface
 *      op (surface op parameters are accessed through the generator panel
 *      via the SceneObject's `generator` ref — the redo panel is the wrong
 *      affordance for them).
 */
function runSurfaceConfirm<K extends SurfaceToolKind>(
  set: Set_,
  get: Get_,
  expectedKind: K,
  pushUndo: () => void,
  createMaterial: () => string,
): void {
  const tool = get().surfaceTool;
  if (!tool || tool.kind !== expectedKind || !tool.draft) {
    if (tool && tool.kind === expectedKind) {
      // Tool armed but no draft — clean disarm.
      set({ surfaceTool: null, activeOperator: null });
    }
    return;
  }

  const builder = SURFACE_TOOL_BUILDERS[expectedKind];
  // The discriminated narrowing on `tool.kind === expectedKind` lets us
  // pass the draft directly to the matching builder.
  const built = (builder as SurfaceToolBuilder<K>)(
    tool.draft as SurfaceDraftFor<K>,
    {
      sceneObjects: get().sceneObjects,
      createMaterial,
    },
  );
  if (!built) {
    // Validation rejected the draft — disarm cleanly without touching the
    // undo stack. Clearing `activeOperator` here matches the legacy
    // per-kind confirms (Box/Cyl/Poly called `armToolOperator(null)`;
    // Inset cleared the field inline) and ensures the operator palette
    // toggle and the next pointerdown both see a fully-disarmed state.
    set({ surfaceTool: null, activeOperator: null });
    return;
  }

  const surfaceCommit = commitSurfaceObjectWithOptionalBoolean({
    sceneObjects: get().sceneObjects,
    collections: get().collections,
    modifierOverrides: get().modifierOverrides,
    visibility: get().visibility,
    renderVisibility: get().renderVisibility,
    cutterObject: built.object,
    hostObjectId: built.hostObjectId,
    reverse: built.reverse,
  });

  pushUndo();
  clearLiveEditPreview();

  const modifierOverrides = applyGeneratorModifiers(
    surfaceCommit.modifierOverrides,
    built.object.id,
    built.generatorModifiers,
  );

  set({
    sceneObjects: surfaceCommit.sceneObjects,
    collections: surfaceCommit.collections,
    modifierOverrides,
    visibility: surfaceCommit.visibility,
    renderVisibility: surfaceCommit.renderVisibility,
    selectedObjectTargets: surfaceCommit.selectedObjectTargets,
    mode: "transform" as InteractionMode,
    editMode: null,
    activeEditSession: null,
    surfaceTool: null,
    activeOperator: null,
    // Surface op parameters live in the generator system (accessed via the
    // standard generator panel + the SceneObject's `generator` ref), not
    // the redo panel. Clearing here suppresses any redo panel that a prior
    // op would otherwise leave on screen after this confirm.
    lastOperation: null,
  });
}

// ---------------------------------------------------------------------------
// Slice mixin
// ---------------------------------------------------------------------------

/**
 * Surface tool store actions. Inject into the Zustand store factory the
 * same way `createObjectOps` / `createCommandOps` are injected.
 *
 * Required dependencies (passed as closures so the slice does not import
 * the rest of the store):
 *   - `pushUndo` — capture an undo entry before applying the surface op
 *   - `createMaterial` — allocate a new SceneMaterial id for the new object
 */
export function createSurfaceOps(
  set: Set_,
  get: Get_,
  deps: {
    pushUndo: () => void;
    createMaterial: () => string;
  },
) {
  return {
    setSurfaceTool: (next: SurfaceToolState | null) => {
      set({ surfaceTool: next });
    },

    confirmSurfaceBox: () => {
      runSurfaceConfirm(set, get, "box", deps.pushUndo, deps.createMaterial);
    },

    confirmSurfaceCylinder: () => {
      runSurfaceConfirm(
        set,
        get,
        "cylinder",
        deps.pushUndo,
        deps.createMaterial,
      );
    },

    confirmSurfacePoly: () => {
      runSurfaceConfirm(set, get, "poly", deps.pushUndo, deps.createMaterial);
    },

    confirmSurfaceInset: () => {
      runSurfaceConfirm(set, get, "inset", deps.pushUndo, deps.createMaterial);
    },
  };
}
